/**
 * SOVRANOVA — Guided Hypnosis Player v4
 *
 * 4 DISTINCT EXPERIENCES:
 *
 * GUIDED     → Voice only. Welcome → induction → deepening → suggestion → emergence.
 *              Clean, minimal UI. No tone mentioned anywhere. Eyes closed.
 *
 * FREQUENCY  → Voice + a soft background tone together.
 *              Tone starts first (fade-in), then voice begins. Headphones recommended.
 *
 * SLEEP      → Voice induction that fades and slows, then the soft tone continues.
 *              No count-up emergence. No "Complete" state. Runs until stopped.
 *
 * SUBCONSCIOUS → No voice. A soft theta tone + slowly scrolling affirmations.
 *
 * AUDIO: The script is split on [[pause:N]] markers; every spoken segment is prefetched,
 *        then played back-to-back with EXACT client-side pauses. Real pacing, no network
 *        gaps mid-session, and the full emergence always plays. Audio is unlocked inside
 *        the Begin tap so it autoplays on iOS/Android after that single gesture.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../services/api'
import { useSettings } from '../../hooks/useSettings'
import { useWakeLock } from '../../hooks/useSessionResilience'
import { DimVeil } from '../common/DimVeil'

// Spoken over the grounding screen. Kept as a module constant so it is greppable and
// reviewable rather than buried in JSX. Deliberately does NOT assert anything we cannot
// know ("you're safe"); it only directs attention to what is actually in the room.
const GROUNDING_SPOKEN =
  "Let's come back to the room. Open your eyes when you're ready... " +
  "feel where your body meets the floor beneath you... and let one slow breath out. " +
  "There's no rush at all."

// ── Frequency engine ──────────────────────────────────────────────────────────
function createFreqEngine(hz) {
  const CARRIER = 200
  const isBinaural = hz < 20
  let ctx = null, nodes = []
  return {
    start(volumeTarget = 0.12) {
      if (ctx) return
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (isBinaural) {
        // Speaker-safe low pad. A binaural beat only "works" on headphones; on
        // speakers the two detuned tones mix into an audible flutter/vibration. We
        // make no entrainment claim, so a gentle continuous, consonant tone is honest
        // and calm on any output. (hz here is sub-audible, e.g. 7 — we voice the warm
        // carrier instead of trying to render the beat.)
        const master = ctx.createGain()
        master.gain.setValueAtTime(0, ctx.currentTime)
        master.gain.linearRampToValueAtTime(volumeTarget, ctx.currentTime + 8)
        master.connect(ctx.destination)
        const partials = [{ f: CARRIER, g: 0.5 }, { f: CARRIER * 1.5, g: 0.25 }]
        const oscNodes = []
        partials.forEach(p => {
          const o = ctx.createOscillator(), g = ctx.createGain()
          o.type = 'sine'; o.frequency.setValueAtTime(p.f, ctx.currentTime)
          g.gain.setValueAtTime(p.g, ctx.currentTime)
          o.connect(g); g.connect(master); o.start()
          oscNodes.push(o, g)
        })
        // Slow breathing swell so the pad feels alive, not static (no beat).
        const lfo = ctx.createOscillator(), lfoGain = ctx.createGain()
        lfo.type = 'sine'; lfo.frequency.setValueAtTime(0.07, ctx.currentTime)
        lfoGain.gain.setValueAtTime(volumeTarget * 0.2, ctx.currentTime)
        lfo.connect(lfoGain); lfoGain.connect(master.gain)
        lfo.start()
        nodes = [...oscNodes, lfo, lfoGain, master]
      } else {
        // Warm ambient pad (not a thin single sine): the fundamental + a sub-octave for
        // body + a slightly detuned layer for slow chorus shimmer, softened through a
        // lowpass and gently "breathing" via a slow LFO on the master gain.
        const master = ctx.createGain()
        master.gain.setValueAtTime(0, ctx.currentTime)
        master.gain.linearRampToValueAtTime(volumeTarget, ctx.currentTime + 8)

        const lp = ctx.createBiquadFilter()
        lp.type = 'lowpass'
        lp.frequency.setValueAtTime(Math.max(400, Math.min(900, hz * 1.6)), ctx.currentTime)
        lp.Q.setValueAtTime(0.6, ctx.currentTime)
        lp.connect(master); master.connect(ctx.destination)

        const partials = [
          { f: hz,       g: 0.5 },   // fundamental
          { f: hz / 2,   g: 0.5 },   // sub-octave — warmth and body
          { f: hz + 0.3, g: 0.35 },  // slight detune — slow, soft shimmer
        ]
        const oscNodes = []
        partials.forEach(p => {
          const o = ctx.createOscillator(), g = ctx.createGain()
          o.type = 'sine'; o.frequency.setValueAtTime(p.f, ctx.currentTime)
          g.gain.setValueAtTime(p.g, ctx.currentTime)
          o.connect(g); g.connect(lp)
          o.start()
          oscNodes.push(o, g)
        })

        // Gentle breathing swell so the pad feels alive, not static.
        const lfo = ctx.createOscillator(), lfoGain = ctx.createGain()
        lfo.type = 'sine'; lfo.frequency.setValueAtTime(0.07, ctx.currentTime)
        lfoGain.gain.setValueAtTime(volumeTarget * 0.25, ctx.currentTime)
        lfo.connect(lfoGain); lfoGain.connect(master.gain)
        lfo.start()

        nodes = [...oscNodes, lfo, lfoGain, master, lp]
      }
    },
    stop() {
      if (!ctx) return
      nodes.forEach(n => {
        if (n instanceof GainNode) {
          try { n.gain.cancelScheduledValues(ctx.currentTime); n.gain.setValueAtTime(n.gain.value, ctx.currentTime); n.gain.linearRampToValueAtTime(0, ctx.currentTime + 4) } catch {}
        }
      })
      setTimeout(() => {
        nodes.forEach(n => { try { if (n instanceof OscillatorNode) n.stop() } catch {} })
        try { ctx.close() } catch {}
        ctx = null; nodes = []
      }, 4200)
    },
  }
}

// ── Nidra ambience engine ─────────────────────────────────────────────────────
// The water under the voice. Same start()/stop() shape as createFreqEngine so the
// player can hold either in freqRef and every pause/stop/ground path works
// unchanged. Generative like the tone engine — no audio file, nothing to fetch,
// nothing that can fail mid-session: a looped buffer of pink-ish noise shaped
// through two filters — a low body (the fall itself) and a quieter band of hiss
// (the spray above it) — with the same 0.07 Hz swell the tone engine breathes at,
// so the two background modes share one bodily tempo.
// Scene voice recipes. Each is [filterType, freq, Q, gain] over the same pink
// buffer — waterfall is the original two-voice fall; rain sits whiter and higher
// with a fine hiss; forest is a low wind with a soft band of leaf rustle (plus
// sparse synthesized birdsong, gated below).
const AMBIENT_SCENES = {
  // v2 recipes: much less raw noise bed, more character per scene.
  // waterfall = layered wash + a low churning gurgle (two detuned slow LFOs on a 150 Hz band)
  // rain      = soft hiss bed + a pre-rendered loop of individual droplet "plinks"
  // forest    = quiet wind with slow gusts + sparse, musical birdsong
  waterfall: { label: 'Waterfall', icon: '🌊', voices: [['lowpass', 280, 0.5, 0.85], ['bandpass', 950, 0.6, 0.3], ['bandpass', 2300, 0.8, 0.08]], gurgle: true },
  rain:      { label: 'Rain',      icon: '🌧️', voices: [['lowpass', 900, 0.5, 0.3], ['bandpass', 5000, 0.6, 0.1]], droplets: true },
  forest:    { label: 'Forest',    icon: '🌲', voices: [['lowpass', 200, 0.5, 0.55], ['bandpass', 1900, 1.2, 0.05]], gusts: true, birds: true },
}

function createAmbienceEngine(scene = 'waterfall') {
  const recipe = AMBIENT_SCENES[scene] || AMBIENT_SCENES.waterfall
  let ctx = null, nodes = [], birdTimer = null
  return {
    start(volumeTarget = 0.12) {
      if (ctx) return
      ctx = new (window.AudioContext || window.webkitAudioContext)()

      // REAL RECORDING FIRST. Each scene has a seamless 60s loop of an actual
      // field recording in /sounds (CC0/public-domain, loudness-normalised to
      // -24 LUFS). Crystal clear where synthesis reads as cabin noise. The
      // generative synth below survives only as the offline/fetch-failure
      // fallback, so a session can still never lose its background mid-flight.
      const fileMaster = ctx.createGain()
      fileMaster.gain.setValueAtTime(0, ctx.currentTime)
      fileMaster.connect(ctx.destination)
      nodes.push(fileMaster)
      fetch(`/sounds/ambient-${scene}.mp3`)
        .then(r => { if (!r.ok) throw new Error('no file'); return r.arrayBuffer() })
        .then(ab => new Promise((res, rej) => ctx.decodeAudioData(ab, res, rej)))
        .then(buf => {
          if (!ctx) return
          const src = ctx.createBufferSource()
          src.buffer = buf; src.loop = true
          src.connect(fileMaster)
          src.start(0, Math.random() * Math.max(1, buf.duration - 5))
          nodes.push(src)
          // -24 LUFS source × this gain sits clearly beneath the voice while
          // staying fully articulate.
          fileMaster.gain.linearRampToValueAtTime(Math.min(1, volumeTarget * 3), ctx.currentTime + 6)
        })
        .catch(() => { try { if (ctx) startSynth() } catch {} })

      const startSynth = () => {
      // 4s looped noise buffer. Paul Kellet's economy pink-noise filter, so the
      // spectrum falls away like distant water rather than hissing like static.
      const len = ctx.sampleRate * 4
      const buf = ctx.createBuffer(1, len, ctx.sampleRate)
      const data = buf.getChannelData(0)
      let b0 = 0, b1 = 0, b2 = 0
      for (let i = 0; i < len; i++) {
        const white = Math.random() * 2 - 1
        b0 = 0.997 * b0 + 0.029591 * white
        b1 = 0.985 * b1 + 0.032534 * white
        b2 = 0.950 * b2 + 0.048056 * white
        data[i] = (b0 + b1 + b2 + white * 0.05) * 0.55
      }
      const master = ctx.createGain()
      master.gain.setValueAtTime(0, ctx.currentTime)
      master.gain.linearRampToValueAtTime(volumeTarget, ctx.currentTime + 8)
      master.connect(ctx.destination)

      const makeVoice = (type, freq, q, gain) => {
        const src = ctx.createBufferSource()
        src.buffer = buf; src.loop = true
        const f = ctx.createBiquadFilter()
        f.type = type; f.frequency.setValueAtTime(freq, ctx.currentTime); f.Q.setValueAtTime(q, ctx.currentTime)
        const g = ctx.createGain(); g.gain.setValueAtTime(gain, ctx.currentTime)
        src.connect(f); f.connect(g); g.connect(master)
        // Offset start points so the two voices never phase-align into a pattern.
        src.start(0, Math.random() * 2)
        return [src, f, g]
      }
      const voiceGroups = recipe.voices.map(v => makeVoice(...v))

      const lfo = ctx.createOscillator(), lfoGain = ctx.createGain()
      lfo.type = 'sine'; lfo.frequency.setValueAtTime(0.07, ctx.currentTime)
      lfoGain.gain.setValueAtTime(volumeTarget * 0.2, ctx.currentTime)
      lfo.connect(lfoGain); lfoGain.connect(master.gain)
      lfo.start()
      nodes.push(...voiceGroups.flat(), lfo, lfoGain, master)

      // A slow modulator: sine LFO of freq `fr` and depth `depth` wired into `param`.
      const slowMod = (param, fr, depth) => {
        const l = ctx.createOscillator(), lg = ctx.createGain()
        l.type = 'sine'; l.frequency.setValueAtTime(fr, ctx.currentTime)
        lg.gain.setValueAtTime(depth, ctx.currentTime)
        l.connect(lg); lg.connect(param); l.start()
        nodes.push(l, lg)
      }

      // Waterfall: a low churning band whose level is wobbled by two detuned slow
      // LFOs — the irregular "gulping" a real fall has under its wash.
      if (recipe.gurgle) {
        const [gs, gf, gg] = makeVoice('lowpass', 150, 0.7, 0.35)
        slowMod(gg.gain, 0.31, 0.16)
        slowMod(gg.gain, 0.53, 0.11)
        nodes.push(gs, gf, gg)
      }

      // Rain: a pre-rendered 8-second loop of individual droplet "plinks" — short
      // ringing sine transients at random pitches — over the soft hiss bed. This is
      // what reads as rain rather than static.
      if (recipe.droplets) {
        const dlen = ctx.sampleRate * 8
        const dbuf = ctx.createBuffer(1, dlen, ctx.sampleRate)
        const dd = dbuf.getChannelData(0)
        const drops = 8 * 55
        for (let d = 0; d < drops; d++) {
          const at = Math.floor(Math.random() * (dlen - 4000))
          const f = 2200 + Math.random() * 4800
          const amp = 0.05 + Math.random() * 0.13
          const tau = ctx.sampleRate * (0.002 + Math.random() * 0.006)
          const n = Math.floor(tau * 6)
          const w = (2 * Math.PI * f) / ctx.sampleRate
          for (let i = 0; i < n && at + i < dlen; i++) {
            dd[at + i] += Math.sin(w * i) * amp * Math.exp(-i / tau)
          }
        }
        const dsrc = ctx.createBufferSource()
        dsrc.buffer = dbuf; dsrc.loop = true
        const dg = ctx.createGain(); dg.gain.setValueAtTime(0.9, ctx.currentTime)
        dsrc.connect(dg); dg.connect(master)
        dsrc.start(0, Math.random() * 4)
        nodes.push(dsrc, dg)
      }

      // Forest: slow gusts — deep, uneven swells on the wind bed itself.
      if (recipe.gusts && voiceGroups[0]) {
        const windGain = voiceGroups[0][2]
        slowMod(windGain.gain, 0.05, 0.2)
        slowMod(windGain.gain, 0.023, 0.14)
      }

      // Forest only: sparse, musical birdsong — a short phrase of 3–5 warbled notes
      // every 6–15 seconds, soft enough to read as depth in the trees, never an event.
      if (recipe.birds) {
        const chirp = () => {
          if (!ctx) return
          try {
            let t = ctx.currentTime + 0.05
            const notes = 3 + Math.floor(Math.random() * 3)
            const f0 = 2300 + Math.random() * 1500
            for (let n = 0; n < notes; n++) {
              const o = ctx.createOscillator(), g = ctx.createGain()
              // Each note starts near the phrase pitch and warbles up-then-down —
              // closer to song than to a beep.
              const f = f0 * (0.92 + Math.random() * 0.16)
              const dur = 0.08 + Math.random() * 0.06
              o.type = 'sine'
              o.frequency.setValueAtTime(f, t)
              o.frequency.exponentialRampToValueAtTime(f * 1.25, t + dur * 0.4)
              o.frequency.exponentialRampToValueAtTime(f * 0.9, t + dur)
              g.gain.setValueAtTime(0, t)
              g.gain.linearRampToValueAtTime(volumeTarget * 0.22, t + dur * 0.25)
              g.gain.linearRampToValueAtTime(0, t + dur)
              o.connect(g); g.connect(master)
              o.start(t); o.stop(t + dur + 0.02)
              t += dur + 0.06 + Math.random() * 0.15
            }
          } catch {}
          birdTimer = setTimeout(chirp, 6000 + Math.random() * 9000)
        }
        birdTimer = setTimeout(chirp, 3000 + Math.random() * 4000)
      }
      } // end startSynth (fallback only — the real recording is the primary path)
    },
    stop() {
      if (!ctx) return
      if (birdTimer) { clearTimeout(birdTimer); birdTimer = null }
      nodes.forEach(n => {
        if (n instanceof GainNode) {
          try { n.gain.cancelScheduledValues(ctx.currentTime); n.gain.setValueAtTime(n.gain.value, ctx.currentTime); n.gain.linearRampToValueAtTime(0, ctx.currentTime + 4) } catch {}
        }
      })
      setTimeout(() => {
        nodes.forEach(n => { try { if (n instanceof OscillatorNode || n instanceof AudioBufferSourceNode) n.stop() } catch {} })
        try { ctx.close() } catch {}
        ctx = null; nodes = []
      }, 4200)
    },
  }
}

// ── base64 → playable blob URL ──────────────────────────────────────────────
function base64ToBlobUrl(b64, mimeType = 'audio/mpeg') {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }))
}

// Async variant that decodes in NATIVE code via a data: URL fetch instead of a
// per-character JS loop. The loop above blocks the main thread for hundreds of
// milliseconds per megabyte — fine before playback, but once windows prefetch
// DURING playback it produced a rhythmic audible stutter every time a segment
// landed (David's "heartbeat" break, 19 Aug: clean for the first window, then
// breaking as window 2+ decoded under the running voice). Falls back to the
// sync path if data:-URL fetch is ever unavailable.
async function base64ToBlobUrlAsync(b64, mimeType = 'audio/mpeg') {
  try {
    const blob = await (await fetch(`data:${mimeType};base64,${b64}`)).blob()
    return URL.createObjectURL(blob)
  } catch {
    return base64ToBlobUrl(b64, mimeType)
  }
}

// ── A short, valid silent WAV used to unlock audio inside a user tap ──────────
// Playing this on a reused <audio> element inside the Begin click satisfies the
// iOS/Android user-gesture requirement, so the real segments autoplay afterwards.
const SILENT_WAV = (() => {
  try {
    const sampleRate = 8000, ms = 60, samples = Math.floor(sampleRate * ms / 1000)
    const buf = new ArrayBuffer(44 + samples * 2)
    const v = new DataView(buf)
    const wr = (o, str) => { for (let i = 0; i < str.length; i++) v.setUint8(o + i, str.charCodeAt(i)) }
    wr(0, 'RIFF'); v.setUint32(4, 36 + samples * 2, true); wr(8, 'WAVE'); wr(12, 'fmt ')
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true)
    v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true)
    v.setUint16(32, 2, true); v.setUint16(34, 16, true); wr(36, 'data'); v.setUint32(40, samples * 2, true)
    let bin = ''; const bytes = new Uint8Array(buf)
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return 'data:audio/wav;base64,' + btoa(bin)
  } catch { return '' }
})()

// ── Fetch all spoken segments for a script (each with the silence to hold after) ──
async function fetchSegments(script, extra = {}, apiBase = '/hypnosis') {
  const res = await api.post(`${apiBase}/full-audio`, { script, ...extra })
  const raw = (res && res.segments) || []
  // Decode sequentially with the native async path, yielding between segments,
  // so a window arriving mid-playback never blocks the main thread long enough
  // to be heard. See base64ToBlobUrlAsync.
  const segments = []
  for (const seg of raw) {
    segments.push({
      text: seg.text || '',
      pauseAfter: Number(seg.pauseAfter) || 0,
      audioUrl: seg.audio ? await base64ToBlobUrlAsync(seg.audio, seg.mimeType || res.mimeType || 'audio/mpeg') : null,
    })
    await new Promise(r => setTimeout(r, 0))
  }
  // The counts come back from the server so the client can tell "quiet on purpose"
  // from "the voice never arrived". `voiced` is derived here rather than trusted,
  // because what matters locally is how many segments produced a playable URL.
  return { segments, voiced: segments.filter(s => s.audioUrl).length }
}

// ── One retry per window, then give up honestly ───────────────────────────────
// A segment that failed to synthesise arrives with no audio but its full pauseAfter,
// and playback holds that pause. So a failed line becomes EXACTLY a silence of its
// intended length — indistinguishable from the silence the practice is meant to
// contain. A whole window failing that way is minutes of nothing while the screen
// looks perfectly healthy. That is what this guards.
//
// A window with SOME audio is kept: the server already refuses anything under half,
// and a couple of missing lines is survivable. A window with NONE is a failure.
async function fetchWindowVoiced(script, extra, apiBase) {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const r = await fetchSegments(script, extra, apiBase)
      if (r.voiced > 0) return r
    } catch { /* fall through to the retry, then to the honest failure below */ }
    if (attempt === 1) await new Promise(res => setTimeout(res, 600))
  }
  return { segments: [], voiced: 0 }
}

// ── Split a script into windows so the first can play while the rest are prepared ──
// Each "unit" is a span of text plus its trailing [[pause:N]] marker; windows are made
// of whole units, so joining all windows reproduces the original script exactly and
// every pause is preserved. The first window is small (fast time-to-first-word); the
// rest are larger to keep the synth comfortably ahead of playback. Returns [script]
// unchanged when there is nothing to gain (very short script or no pause markers).
function splitScriptIntoWindows(script, firstN = 3, restN = 8) {
  const parts = String(script).split(/(\[\[pause:\d+(?:\.\d+)?\]\])/gi)
  const units = []
  for (let i = 0; i < parts.length; i += 2) {
    const unit = (parts[i] || '') + (parts[i + 1] || '')
    if (unit) units.push(unit)
  }
  if (units.length <= firstN) return [script]
  const windows = [units.slice(0, firstN).join('')]
  for (let i = firstN; i < units.length; i += restN) windows.push(units.slice(i, i + restN).join(''))
  return windows
}

// ── Mode configs ──────────────────────────────────────────────────────────────
const MODE_CONFIG = {
  guided:        { label: 'Guided Session',       freq: false, freqFirst: false, sleepMode: false, noVoice: false, hz: null },
  frequency:     { label: 'Frequency-Enhanced',   freq: true,  freqFirst: true,  sleepMode: false, noVoice: false },
  sleep:         { label: 'Sleep Session',         freq: true,  freqFirst: false, sleepMode: true,  noVoice: false },
  subconscious:  { label: 'Tone & Affirmations',  freq: true,  freqFirst: true,  sleepMode: true,  noVoice: true  },
  // Voice-only like guided, but its own entry so the header names the practice
  // (before this, a Yoga Nidra session was labelled "GUIDED SESSION") and so the
  // player can dress the room for it — see NidraScene. `ambience` selects the
  // generative water engine instead of the hz tone — see createAmbienceEngine.
  nidra:         { label: 'Yoga Nidra',           freq: false, freqFirst: false, sleepMode: false, noVoice: false, hz: null, ambience: true },
}

// A quiet 1-5 feeling check-in (a mood, not a clinical measure). Optional, one tap.
const MOODS = [
  { v: 1, emoji: '😣', label: 'Rough' },
  { v: 2, emoji: '😕', label: 'Low' },
  { v: 3, emoji: '😐', label: 'Okay' },
  { v: 4, emoji: '🙂', label: 'Good' },
  { v: 5, emoji: '😌', label: 'Calm' },
]

// ── Nidra scene ───────────────────────────────────────────────────────────────
// Yoga Nidra gets an IMMERSIVE room where every other mode gets a flat gradient.
// The reference experience for this practice is a session played over a slow,
// full-screen nature scene; what is replicated here is the EFFECT — somewhere calm
// for the eyes to rest before they close, nothing to read, nothing to do — not the
// stock footage. The scene is generative and entirely CSS: drifting underwater
// light, a faint falling veil of water, and a glow that breathes at roughly a
// resting exhale's pace. No assets, no network, nothing that can fail mid-session.
//
// Two deliberate non-replications:
//   - Controls stay fully visible. The reference hides everything behind the scene
//     but "Bring me back" must never need hunting for by someone surfacing from
//     deep relaxation.
//   - prefers-reduced-motion stops every animation (the scene stays, still).
// pointerEvents:none + aria-hidden: it is scenery, never a target.
function NidraScene() {
  return (
    <div style={s.nidraScene} aria-hidden="true">
      <div className="sov-nidra-drift1" style={s.nidraDrift1} />
      <div className="sov-nidra-drift2" style={s.nidraDrift2} />
      <div className="sov-nidra-veil"   style={s.nidraVeil} />
      <div className="sov-nidra-glow"   style={s.nidraGlow} />
    </div>
  )
}

function MoodRow({ label, value, onPick }) {
  return (
    <div style={s.moodWrap}>
      <p style={s.moodLabel}>{label}</p>
      <div style={s.moodRow}>
        {MOODS.map(m => (
          <button key={m.v} onClick={() => onPick(m.v)} aria-label={m.label}
            style={{ ...s.moodBtn, ...(value === m.v ? s.moodBtnOn : {}) }}>
            <span style={s.moodEmoji}>{m.emoji}</span>
            <span style={s.moodWord}>{m.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export function GuidedHypnosisPlayer({ topic, mode, minutes = 5, onClose, onHome, onComplete, apiBase = '/hypnosis' }) {
  // Read-only here: used solely to decide whether the grounding exit may speak.
  // Session playback itself is deliberately unchanged by this.
  const { settings } = useSettings()
  const [phase,       setPhase]       = useState('generating')
  const [script,      setScript]      = useState('')
  const [chunks,      setChunks]      = useState([])
  const [affirmations,setAffirmations]= useState([])
  const [afIdx,       setAfIdx]       = useState(0)
  const [sessionMeta, setSessionMeta] = useState(null)
  const [error,       setError]       = useState(null)
  const [spokenLine,  setSpokenLine]  = useState('')
  const [elapsed,     setElapsed]     = useState(0)
  const [moodBefore,  setMoodBefore]  = useState(null)
  const [moodAfter,   setMoodAfter]   = useState(null)
  const [paused,      setPaused]      = useState(false)
  const [retry,       setRetry]       = useState(0)

  // Keep the phone's screen from sleeping for the whole audible session —
  // preparing, playing (incl. pauses) and the grounding exit. A screen
  // timeout mid-Nidra suspends web audio on mobile and kills the session
  // (user request 19 Aug: nothing a user is actively using may time out).
  // The DimVeil in the JSX below handles the battery side: after 30s of no
  // touch the screen fades to near-black while the session keeps flowing.
  useWakeLock(phase === 'preparing' || phase === 'playing' || phase === 'grounding')

  const freqRef    = useRef(null)
  const prefetchRef = useRef(null) // { key, promise } — first voice window, warmed on the ready screen
  const streamRef  = useRef(null)  // { done, full, promise } — script generation, possibly still streaming
  const phaseRef   = useRef('generating')
  useEffect(() => { phaseRef.current = phase }, [phase])
  const audioRef   = useRef(null)
  const stopRef    = useRef(false)
  const timerRef   = useRef(null)
  const pauseTimerRef = useRef(null)
  const segUrlsRef = useRef([])
  const groundUrlRef = useRef(null)

  // ── Post-session experience check (SOV-EXP-6) ──────────────────────────────
  // Items are FETCHED, never hardcoded here: the server owns the list so there is
  // exactly one source of truth. A copy in this file is the drift that bit the
  // session-length whitelist (d6fc6e1).
  const [expItems,     setExpItems]     = useState([])
  const [expAnswers,   setExpAnswers]   = useState({})
  const [expSent,      setExpSent]      = useState(false)
  const [expOffer,     setExpOffer]     = useState(false)
  const expSentRef     = useRef(false)
  const expAnswersRef  = useRef({})
  expAnswersRef.current = expAnswers
  const loggedRef  = useRef(false)
  const segsRef    = useRef([])   // growing queue of fetched segments (progressive playback)
  const moreRef    = useRef(null) // promise while later windows are still being prefetched
  const runRef     = useRef(0)    // current play run; stops a stale prefetch from a prior Begin
  const moodBeforeRef = useRef(null)
  const moodAfterRef  = useRef(null)
  const completedRef  = useRef(false) // playback reached the end (drives a single completion write)
  // Set when a later window could not be voiced even after a retry, so the session ends
  // early. Playback still reaches "the end" of what exists, but that is NOT completion —
  // recording it as one would put a short, half-voiced session into the user's own
  // history and into the SOV-SHIFT comparison as if it had been the whole practice.
  const truncatedRef  = useRef(false)
  const pauseRef   = useRef(false) // user paused; halts the segment loop without discarding the queue
  const idxRef     = useRef(0)     // current segment index, so Resume continues from here

  const config = MODE_CONFIG[mode] || MODE_CONFIG.guided

  // Write this session back into memory (once) so SOVRANOVA remembers it next time.
  const logSession = useCallback((completed) => {
    if (loggedRef.current) return
    loggedRef.current = true
    api.post(`${apiBase}/complete`, {
      topic, mode, minutes, completed,
      moodBefore: moodBeforeRef.current,
      moodAfter:  moodAfterRef.current,
    }).catch(() => {})
  }, [topic, mode, minutes, apiBase])

  const pickBefore = (v) => { setMoodBefore(v); moodBeforeRef.current = v }
  const pickAfter  = (v) => { setMoodAfter(v);  moodAfterRef.current  = v }

  // Background-tone level for frequency / sleep / theta modes ("off" mutes the tone).
  // Voice-first default: voice modes (guided/frequency/sleep) start with the tone OFF
  // so there is no synthesized wobble under the voice; the no-voice subconscious mode
  // keeps a soft tone because the tone IS its whole experience. Users can still turn
  // the tone on (Soft/Medium) from the control below.
  // Nidra's water ambience defaults ON: it is broadband noise, not a synth tone, so
  // the wobble concern doesn't apply — and the quiet fall of water under the voice is
  // part of this practice's room, the way the tone is the whole of Silent's. Off
  // stays one tap away for anyone who wants the voice bare.
  const toneDefault = (config.noVoice || config.ambience) ? 'soft' : 'off'
  const [toneLevel, setToneLevel] = useState(toneDefault)
  const toneLevelRef = useRef(toneDefault)
  const pickTone = (lvl) => {
    setToneLevel(lvl); toneLevelRef.current = lvl
    // Ambience modes: apply the level immediately, even mid-session. The engine has
    // no live volume param, so restart it at the new level (its own stop() fades out
    // over 4s, so the change lands as a soft crossfade). Off simply stops it.
    if (config.ambience) {
      if (freqRef.current) { try { freqRef.current.stop() } catch {} ; freqRef.current = null }
      const vol = toneVol()
      if (vol > 0 && phaseRef.current === 'playing') {
        try { freqRef.current = createAmbienceEngine(ambientSceneRef.current); freqRef.current.start(vol) } catch {}
      }
    }
  }

  // Ambient scene (waterfall / rain / forest) for ambience modes. Persisted so the
  // choice survives sessions — this is a room preference, not a per-session whim.
  const sceneDefault = (() => { try { return localStorage.getItem('sov_ambient_scene') || 'waterfall' } catch { return 'waterfall' } })()
  const [ambientScene, setAmbientScene] = useState(sceneDefault)
  const ambientSceneRef = useRef(sceneDefault)
  const pickScene = (sc) => {
    setAmbientScene(sc); ambientSceneRef.current = sc
    try { localStorage.setItem('sov_ambient_scene', sc) } catch {}
    // Mid-session switch: crossfade by stopping the old engine (it fades itself out
    // over 4s) and starting the new scene at the current level.
    if (config.ambience && freqRef.current) {
      try { freqRef.current.stop() } catch {}
      freqRef.current = null
      const vol = toneVol()
      if (vol > 0) { try { freqRef.current = createAmbienceEngine(sc); freqRef.current.start(vol) } catch {} }
    }
  }
  const toneVol = () => {
    const base = config.sleepMode ? 0.10 : config.noVoice ? 0.15 : config.ambience ? 0.12 : 0.07
    return base * ({ off: 0, soft: 1, medium: 2.2 }[toneLevelRef.current] ?? 1)
  }
  // One place decides which background engine this mode owns, so the Begin-gesture
  // start and the startSession guard can never disagree about it.
  const startBackground = useCallback((vol) => {
    if (vol <= 0 || freqRef.current) return
    try {
      if (config.ambience) { freqRef.current = createAmbienceEngine(ambientSceneRef.current); freqRef.current.start(vol) }
      else if (config.freq && sessionMeta?.hz) { freqRef.current = createFreqEngine(sessionMeta.hz); freqRef.current.start(vol) }
    } catch {}
  }, [config, sessionMeta])

  // ── Generate on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    stopRef.current = false

    const modeParam = mode === 'subconscious' ? 'subconscious' : mode

    // Remember the last-used voiced session so the dashboard can quietly
    // pre-generate the next one — see Dashboard's pregenerate effect.
    try {
      if (apiBase === '/meditation') {
        localStorage.setItem('sov_last_session', JSON.stringify({ apiBase, topic, mode: modeParam, minutes }))
      }
    } catch (_) {}

    // Streaming generation. The script arrives token by token; the moment its
    // OPENING (the first three pause-units) is complete, the ready screen shows
    // and the first voice window starts warming — while the rest of the script
    // is still being written. Falls back to the classic POST when the streaming
    // endpoint is absent (hypnosis routes) or fails.
    const st = { done: false, full: null }
    st.promise = new Promise((res, rej) => { st._res = res; st._rej = rej })
    st.promise.catch(() => {}) // avoid unhandled rejection when nobody awaits it
    streamRef.current = st

    const applyFinal = (data) => {
      if (cancelled) return
      st.done = true; st.full = data; st._res(data)
      setScript(data.script || '')
      setChunks(data.chunks || [])
      setSessionMeta(data.session)

      if (mode === 'subconscious') {
        const lines = (data.script || '')
          .replace(/\[\[pause:[\d.]+\]\]/gi, ' ')
          .split('\n')
          .map(l => l.replace(/^[-•*#>]+\s*/, '').trim())
          .filter(l => l.length > 5 && !l.startsWith('#'))
          .slice(0, 40)
        setAffirmations(lines.length > 0 ? lines : data.chunks || [])
      }

      // Only move to ready if we are still on the generating spinner. The final
      // script often lands AFTER the person has already pressed Begin on the
      // early-ready opening — resetting the phase then would kick them out of
      // their own running session (bug found by David, 19 Aug).
      if (phaseRef.current === 'generating') setPhase('ready')
    }
    const fail = () => {
      if (cancelled) return
      st._rej(new Error('generate failed'))
      setError('Could not generate session. Please try again.')
      setPhase('error')
    }
    const fallback = () => {
      api.post(`${apiBase}/generate`, { topic, mode: modeParam, minutes })
        .then(applyFinal)
        .catch(fail)
    }

    let partial = '', early = false, final = null
    api.stream(`${apiBase}/generate-stream`, { topic, mode: modeParam, minutes }, (line) => {
      if (cancelled) return
      if (line.t) {
        partial += line.t
        if (!early && mode !== 'subconscious') {
          const marks = [...partial.matchAll(/\[\[pause:[\d.]+\]\]/gi)]
          if (marks.length >= 4) {
            early = true
            setScript(partial.slice(0, marks[2].index + marks[2][0].length))
            setPhase('ready')
          }
        }
      } else if (line.meta && line.session) {
        setSessionMeta(line.session)
      } else if (line.done) {
        final = line
      }
    }).then(() => {
      if (cancelled) return
      if (final) applyFinal(final)
      else fallback()
    }).catch(() => { if (!cancelled) fallback() })

    return () => { cancelled = true }
  }, [topic, mode, minutes, retry, apiBase])

  // ── Elapsed timer while playing ────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'playing' && !paused) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)
    } else {
      clearInterval(timerRef.current)
    }
    return () => clearInterval(timerRef.current)
  }, [phase, paused])

  // ── Subconscious: rotate affirmations slowly ───────────────────────────────
  useEffect(() => {
    if (phase !== 'playing' || paused || !config.noVoice || affirmations.length === 0) return
    const t = setInterval(() => {
      setAfIdx(i => (i + 1) % affirmations.length)
    }, 6000)
    return () => clearInterval(t)
  }, [phase, paused, config.noVoice, affirmations])

  // Play the (progressively growing) segment queue back-to-back with exact pauses.
  // When playback reaches the end of what's fetched so far, it waits for the remaining
  // windows to arrive before finishing — so nothing is ever cut off mid-session.
  const playFrom = useCallback((startIdx) => {
    let i = startIdx
    const a = audioRef.current
    const finish = () => {
      // Reaching the end of the queue is only completion if the queue was the whole
      // session. When a window could not be voiced we stopped early, so this is the end
      // of what existed, not the end of the practice — and it is recorded as such.
      const whole = !truncatedRef.current
      if (config.sleepMode) {
        logSession(whole)
        setSpokenLine(whole
          ? 'Rest now... let the sound stay with you...'
          : 'The guidance stopped early — rest as long as you like.')
      } else {
        completedRef.current = whole
        freqRef.current?.stop()
        if (!whole) {
          // Log it HERE. The exit paths only call logSession when completedRef is set,
          // so without this a truncated session would vanish from the person's history
          // entirely — and SOVRANOVA forgetting a session someone actually lay down for
          // is a worse failure than the one being reported.
          logSession(false)
          setError('The voice stopped partway through this session. Nothing you did — please try again when you\'re ready.')
        }
        setPhase(whole ? 'complete' : 'error')
      }
    }
    const step = async () => {
      if (stopRef.current || pauseRef.current) return
      if (i >= segsRef.current.length) {
        const pending = moreRef.current
        if (pending) {
          try { await pending } catch {}
          if (stopRef.current || pauseRef.current) return
          if (i >= segsRef.current.length) { finish(); return }
        } else {
          finish(); return
        }
      }
      const seg = segsRef.current[i]
      idxRef.current = i
      setSpokenLine(seg.text || '')
      const advance = () => {
        if (stopRef.current || pauseRef.current) return
        const ms = Math.round((seg.pauseAfter || 0) * 1000)
        i += 1
        idxRef.current = i
        if (ms > 0) { pauseTimerRef.current = setTimeout(step, ms) }
        else step()
      }
      if (!seg.audioUrl || !a) { advance(); return }
      a.onended = advance
      a.onerror = advance
      try {
        a.src = seg.audioUrl
        a.currentTime = 0
        const p = a.play()
        if (p && p.catch) p.catch(() => advance())
      } catch { advance() }
    }
    step()
  }, [config, logSession])

  // Pre-warm the first voice window while the person is still on the ready screen
  // (choosing sounds, marking mood). By the time they tap Begin the opening audio
  // is usually already here, so the "Preparing" wait drops from ~30-40s to a
  // breath or two. Keyed by script so a regenerate can never play stale audio.
  useEffect(() => {
    if (phase !== 'ready' || config.noVoice || !script) return
    const w0 = splitScriptIntoWindows(script)[0]
    // Keyed by the WINDOW text, not the whole script: while streaming, `script` is
    // the opening prefix and later becomes the full script — but the first window
    // is identical in both, so the warm fetch must not run twice.
    if (!w0 || (prefetchRef.current && prefetchRef.current.key === w0)) return
    prefetchRef.current = {
      key: w0,
      // The opening is roughly the first tenth of a session; a fixed early progress
      // keeps the voice ramp right even while the full length is still unknown.
      promise: fetchWindowVoiced(w0, { progressStart: 0, progressEnd: 0.12, mode }, apiBase).catch(() => null),
    }
  }, [phase, script, config, mode, apiBase])

  const startSession = useCallback(async () => {
    stopRef.current = false

    // SUBCONSCIOUS: tone only, no voice (tone already started in onBegin)
    if (config.noVoice) {
      setPhase('playing'); setElapsed(0)
      startBackground(toneVol())
      return
    }

    // VOICE MODES: progressive playback — synthesise the first window, start playing it
    // immediately, and prefetch the remaining windows in the background. This removes the
    // long up-front wait; the first words begin after only a few segments are ready.
    setPhase('preparing')
    const myRun = ++runRef.current
    segsRef.current = []
    moreRef.current = null
    truncatedRef.current = false

    // FIRST WINDOW — usually already warmed from the ready screen, which may have
    // appeared while the script was still streaming. Play it as soon as it exists;
    // the rest of the script can finish writing underneath the opening words.
    let firstRes = null
    const pre = prefetchRef.current
    if (pre) firstRes = await pre.promise
    if (!firstRes || !firstRes.segments || !firstRes.segments.some(x => x.audioUrl)) {
      // No usable warm window: wait for the finished script and fetch its opening.
      let fullNow
      try { fullNow = streamRef.current?.done ? streamRef.current.full : await streamRef.current.promise }
      catch { setError('Could not prepare audio. Please try again.'); setPhase('error'); return }
      const ws = splitScriptIntoWindows(fullNow.script)
      firstRes = await fetchWindowVoiced(ws[0], { progressStart: 0, progressEnd: 0.12, mode }, apiBase)
    }
    const first = firstRes.segments
    if (stopRef.current) return
    if (!first.length || !first.some(x => x.audioUrl)) {
      freqRef.current?.stop()
      setError('Could not prepare audio. Please try again.')
      setPhase('error')
      return
    }
    segsRef.current = first
    segUrlsRef.current = first.map(x => x.audioUrl).filter(Boolean)

    // REMAINING WINDOWS — computed once the full script exists (it may still be
    // streaming while window 1 is already playing; the first window is ~a minute
    // of audio and pauses, which is far longer than the rest of the generation).
    // The run token stops a prefetch from a previous Begin appending stale audio.
    moreRef.current = (async () => {
      let fullData
      try { fullData = streamRef.current?.done ? streamRef.current.full : await streamRef.current.promise }
      catch { truncatedRef.current = true; return }
      if (stopRef.current || runRef.current !== myRun) return
      const fullScript = fullData.script || ''
      const windows = splitScriptIntoWindows(fullScript)
      // Per-window session progress (0..1 by character offset) so the backend can ramp
      // the voice — slowing into the descent, lifting again at a waking emergence.
      const total = Math.max(1, fullScript.length)
      const winRanges = []; let acc = 0
      for (const w of windows) { winRanges.push([acc / total, (acc + w.length) / total]); acc += w.length }
      {
        for (let w = 1; w < windows.length; w++) {
          if (stopRef.current || runRef.current !== myRun) return
          const res = await fetchWindowVoiced(
            windows[w], { progressStart: winRanges[w][0], progressEnd: winRanges[w][1], mode }, apiBase
          )
          if (stopRef.current || runRef.current !== myRun) return
          if (res.voiced === 0) {
            // STOP, rather than carry on. Appending a voiceless window would play its
            // silences with nothing in them; skipping to the NEXT window would jump the
            // person over a stage of the practice without either of us noticing. Ending
            // where the voice ended is the only honest option left, and it is gentler
            // than an error thrown at someone lying down with their eyes closed.
            truncatedRef.current = true
            break
          }
          const more = res.segments
          segsRef.current = segsRef.current.concat(more)
          segUrlsRef.current = segUrlsRef.current.concat(more.map(x => x.audioUrl).filter(Boolean))
        }
      }
      if (runRef.current === myRun) moreRef.current = null
    })()

    setPhase('playing'); setSpokenLine(''); setElapsed(0)

    // Background audio under the voice (tone for frequency/sleep, water for nidra).
    // Started in onBegin; startBackground guards against double-start.
    startBackground(toneVol())
    if (config.freq && config.freqFirst) {
      await new Promise(r => setTimeout(r, config.sleepMode ? 6000 : 3000))
    }
    if (stopRef.current) return

    playFrom(0)
  }, [script, config, playFrom, apiBase, startBackground, mode])

  // Begin = the user gesture. Unlock the audio element + AudioContext synchronously
  // here so playback autoplays on iOS/Android, THEN prefetch + play.
  const onBegin = useCallback(() => {
    stopRef.current = false
    loggedRef.current = false
    completedRef.current = false
    pauseRef.current = false
    idxRef.current = 0
    setPaused(false)
    moodAfterRef.current = null
    setMoodAfter(null)

    // Unlock a reusable <audio> element within the gesture
    let a = audioRef.current
    if (!a) { a = new Audio(); audioRef.current = a }
    try {
      if (SILENT_WAV) {
        a.src = SILENT_WAV
        const p = a.play()
        if (p && p.then) p.then(() => { try { a.pause(); a.currentTime = 0 } catch {} }).catch(() => {})
      }
    } catch {}

    // Start the background audio within the gesture (unlocks AudioContext on iOS)
    startBackground(toneVol())

    startSession()
  }, [config, sessionMeta, startSession, startBackground])

  const handleStop = () => {
    stopRef.current = true
    pauseRef.current = false
    if (completedRef.current) logSession(true)
    else if (phase === 'playing' && elapsed >= 20) logSession(config.noVoice && elapsed >= 60)
    audioRef.current?.pause()
    freqRef.current?.stop()
    freqRef.current = null
    clearInterval(timerRef.current)
    clearTimeout(pauseTimerRef.current)
    setPhase('ready')
    setSpokenLine('')
    setElapsed(0)
    setAfIdx(0)
    setPaused(false)
  }

  // A grounding exit is NOT the same as Stop. Stop ends the session and hands back a
  // menu — which is the wrong thing to give someone who has drifted, feels far away, or
  // just wants out of an inward-turned state. This stops the guiding voice immediately,
  // fades the tone, and puts something simple and concrete in front of them instead.
  // Deliberately no crisis escalation here: the CrisisLifeline pill is already fixed to
  // every screen, and most people who tap this are uncomfortable, not in crisis.
  const handleGround = () => {
    stopRef.current = true
    pauseRef.current = false
    // Logged as started-not-completed, exactly as Stop does. Preference anchors only —
    // never a state, a reason, or anything resembling a clinical observation.
    if (phase === 'playing' && elapsed >= 20) logSession(false)
    audioRef.current?.pause()
    freqRef.current?.stop()
    freqRef.current = null
    clearInterval(timerRef.current)
    clearTimeout(pauseTimerRef.current)
    setPaused(false)
    setSpokenLine('')
    setPhase('grounding')
  }

  // Speak the grounding, best-effort. The on-screen steps are the real thing — this is
  // an enhancement, because eyes are usually closed in these sessions and someone who
  // has drifted may not read anything. Every failure path here is SILENT by design: no
  // error state, no retry, no spinner, nothing that could turn a bad moment into a
  // broken screen. If the network is down or TTS fails, the text stands on its own.
  //
  // Honours textOnly even though the hypnosis session itself does not. The session
  // predates that setting and a user who opens a voiced session has chosen audio; but a
  // person who set text-only and then gets an unexpected voice at their most vulnerable
  // moment is a worse outcome than silence, and they lose nothing — the steps are all on
  // screen. (Flagged for David: hypnosis/vent ignoring textOnly is a separate open call.)
  const speakGrounding = !(settings?.textOnly || settings?.voiceEnabled === false)

  useEffect(() => {
    if (phase !== 'grounding' || !speakGrounding) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.post('/voice/speak', { text: GROUNDING_SPOKEN })
        if (cancelled || !res || !res.audio) return
        const url = base64ToBlobUrl(res.audio, res.mimeType || 'audio/mpeg')
        groundUrlRef.current = url
        // Reuse the element unlocked inside the Begin tap — a fresh Audio() here would
        // be blocked on iOS, because this play() happens after an await and is therefore
        // outside the original user gesture.
        const el = audioRef.current
        if (!el) return
        el.src = url
        const p = el.play()
        if (p && p.catch) p.catch(() => {})
      } catch { /* silent by design */ }
    })()
    return () => {
      cancelled = true
      try { audioRef.current?.pause() } catch {}
      if (groundUrlRef.current) {
        try { URL.revokeObjectURL(groundUrlRef.current) } catch {}
        groundUrlRef.current = null
      }
    }
  }, [phase, speakGrounding])

  // Fetch the item list lazily — only once a session actually completes, so an
  // abandoned session costs nothing. Failure is silent: no items, no section, no error.
  useEffect(() => {
    if (phase !== 'complete' || expItems.length > 0) return
    let cancelled = false
    api.get('/outcomes/experience-check')
      .then(r => { if (!cancelled && r && Array.isArray(r.items)) setExpItems(r.items) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [phase, expItems.length])

  // Submit the check. Safe to call twice — the ref guard means the "send on the way out"
  // path below can't double-post what the Done button already sent.
  const sendExperienceCheck = useCallback(async (opts = {}) => {
    if (expSentRef.current) return
    const answers = expAnswersRef.current
    if (!opts.force && Object.keys(answers).length === 0) return
    expSentRef.current = true
    try {
      const r = await api.post('/outcomes/experience-check', {
        responses: answers, mode, topic,
      })
      setExpSent(true)
      if (r && r.offerGrounding) setExpOffer(true)
    } catch {
      // Silent. A failed safety log must never become the user's problem, and it must
      // never block them leaving the screen.
      setExpSent(true)
    }
  }, [mode, topic])

  // Pause keeps the session intact: halt the voice + timers, leave the queue (and any
  // background prefetch) in place. Resume picks up from the current segment.
  const handlePause = () => {
    pauseRef.current = true
    setPaused(true)
    audioRef.current?.pause()
    clearTimeout(pauseTimerRef.current)
  }

  const handleResume = () => {
    if (!pauseRef.current) return
    pauseRef.current = false
    setPaused(false)
    playFrom(idxRef.current)
  }

  // Recover from a failure without dead-ending. If the script already generated (so the
  // failure was audio prep), go back to ready and let Begin retry the audio. Otherwise
  // regenerate from scratch by bumping the retry token the generate effect depends on.
  const handleRetry = () => {
    setError(null)
    stopRef.current = false
    if (script) {
      setPhase('ready')
    } else {
      setPhase('generating')
      setRetry(r => r + 1)
    }
  }

  useEffect(() => {
    return () => {
      stopRef.current = true
      if (completedRef.current) logSession(true)
      audioRef.current?.pause()
      freqRef.current?.stop()
      clearInterval(timerRef.current)
      clearTimeout(pauseTimerRef.current)
      segUrlsRef.current.forEach(u => { try { URL.revokeObjectURL(u) } catch {} })
      segUrlsRef.current = []
    }
  }, [])

  const fmtTime = s => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`

  // Only show frequency info for modes that actually USE a frequency. Keyed off the
  // config rather than "not guided": nidra is also voice-only (config.freq false, no
  // tone ever starts), and the old test showed it a "Soft background tone" tag for a
  // tone that never played.
  const showFreq = config.freq && sessionMeta?.hz

  return (
    <div style={s.page(mode)}>
      {/* Battery-friendly low-dark mode while the session plays hands-off.
          The wake lock (above) keeps the phone awake; this keeps it dark. */}
      <DimVeil active={phase === 'playing' && !paused} />
      {mode === 'nidra' && <NidraScene />}
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => { handleStop(); onClose?.() }}>&#8592; Back</button>
        <p style={s.modeTag}>{config.label.toUpperCase()}</p>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          {phase === 'playing'
            ? <span style={s.liveTag}>&#9679; {fmtTime(elapsed)}</span>
            : null}
          <button style={s.homeBtn} onClick={() => { handleStop(); onHome?.() }} title="Dashboard">⌂</button>
        </div>
      </div>

      {/* ── ERROR ── */}
      {phase === 'error' && (
        <div style={s.centre}>
          <p style={{ color:'#e88', textAlign:'center', marginBottom:'1rem' }}>{error}</p>
          <button style={s.retryBtn} onClick={handleRetry}>Try again</button>
        </div>
      )}

      {/* ── GROUNDING ── a calm, concrete exit, not a crisis screen ── */}
      {phase === 'grounding' && (
        <div style={s.groundWrap}>
          <p style={s.groundTitle}>Let's come back to the room.</p>
          <p style={s.groundLead}>No rush. Take these one at a time.</p>
          <ol style={s.groundList}>
            <li style={s.groundStep}>Open your eyes, and let them rest wherever they land.</li>
            <li style={s.groundStep}>Feel where your body meets the floor, the bed, or the chair.</li>
            <li style={s.groundStep}>Name five things you can see around you.</li>
            <li style={s.groundStep}>Name four things you can hear.</li>
            <li style={s.groundStep}>Press your feet down, and let one slow breath out.</li>
          </ol>
          <p style={s.groundNote}>
            Stopping a session partway is a good decision, not a failed one. Relaxing inward
            doesn't suit everyone on every day, and noticing that is worth more than pushing on.
          </p>
          <button style={s.groundHome} onClick={() => { freqRef.current?.stop(); onHome?.() }}>
            I'm okay — take me back
          </button>
        </div>
      )}

      {/* ── GENERATING ── */}
      {phase === 'generating' && (
        <div style={s.centre}>
          <div style={s.spinner} />
          <p style={s.genTitle}>Personalising your session...</p>
          <p style={s.genSub}>Tailoring every word to you</p>
        </div>
      )}

      {/* ── PREPARING (synthesising full seamless audio) ── */}
      {phase === 'preparing' && (
        <div style={s.centre}>
          <div style={s.spinner} />
          <p style={s.genTitle}>Preparing your session...</p>
          <p style={s.genSub}>Recording your voice guidance and pacing — this takes a moment</p>
        </div>
      )}

      {/* ── READY / PLAYING / COMPLETE ── */}
      {['ready','playing','complete'].includes(phase) && sessionMeta && (
        <>
          {/* Session identity */}
          <div style={s.sessionHeader}>
            <p style={s.sessionName}>{sessionMeta.name}</p>
            {showFreq && (
              <p style={s.freqTag}>
                {/* One honest label for every tone: the engine plays a soft pad
                    (a 200 Hz carrier for the sub-20 Hz catalogue entries, a
                    filtered tone otherwise) — never a binaural beat, never an
                    entrainment claim (4 Sep 2026 audit, F3). */}
                Soft background tone
              </p>
            )}
            {config.ambience && toneLevel !== 'off' && (
              <p style={s.freqTag}>{AMBIENT_SCENES[ambientScene]?.icon} {toneLevel === 'medium' ? 'Medium' : 'Soft'} {AMBIENT_SCENES[ambientScene]?.label.toLowerCase()} beneath the voice</p>
            )}
          </div>

          {/* ── SUBCONSCIOUS mode: affirmation display ── */}
          {config.noVoice && (
            <div style={s.subconsciousWrap}>
              {phase === 'ready' && (
                <>
                  <p style={s.subconsciousIntro}>
                    A soft background tone with slowly scrolling affirmations.
                    {'\n\n'}No voice. Best used while awake in deep relaxation — let your eyes rest on each phrase as it appears. The tone is simply something soft to rest in; the affirmations give your mind something gentle to rest on.
                  </p>
                  <div style={s.affirmPreview}>
                    {affirmations.slice(0,3).map((a,i) => (
                      <p key={i} style={{ ...s.affirmLine, opacity: 0.3 + i * 0.2 }}>{a}</p>
                    ))}
                  </div>
                  {/* Moved in from after the controls row for the same reason as
                      the sleep note — out there it would now be clipped. */}
                  <p style={{ ...s.modeNote, paddingBottom: 0, marginTop: '1.5rem' }}>
                    Find a comfortable, relaxed position. A soft tone plays while the affirmations scroll. Best used while awake and resting, rather than asleep.
                  </p>
                </>
              )}
              {phase === 'playing' && (
                <div style={s.affirmActive}>
                  <p style={s.affirmCurrent}>{affirmations[afIdx]}</p>
                  <p style={s.affirmNext}>{affirmations[(afIdx + 1) % affirmations.length]}</p>
                  <p style={s.affirmTimer}>{fmtTime(elapsed)}</p>
                  <div style={s.freqPulse} />
                </div>
              )}
            </div>
          )}

          {/* ── GUIDED / FREQUENCY / SLEEP: script display ── */}
          {!config.noVoice && (
            <div style={s.scriptArea}>
              {phase === 'ready' && (
                <div style={s.readyWrap}>
                  <p style={s.scriptPreview}>
                    {mode === 'sleep'
                      ? 'Your personalised sleep induction is ready. Place your device beside you, dim the screen, and press Begin. My voice will guide you down toward sleep...'
                      : mode === 'frequency'
                      ? 'A soft background tone will begin first, then my voice will join it. Find a comfortable position. Headphones recommended. Eyes closed when ready...'
                      : mode === 'nidra'
                      ? 'Your Yoga Nidra is ready. Lie down on your back somewhere warm and still — a blanket helps. Set your device beside you; once you press Begin there is nothing to look at — only the soft sounds of nature, and a voice to follow at the edge of sleep...'
                      : 'Your session is personalised and ready. Find a comfortable position. Eyes closed when you\'re ready to begin...'}
                  </p>
                  <MoodRow label="Before we begin — how are you feeling?" value={moodBefore} onPick={pickBefore} />
                  <p style={s.cautionLine}>{mode === 'sleep'
                    ? 'Settle in bed where you can safely fall asleep. You can stop anytime.'
                    : mode === 'nidra'
                    ? 'Lie somewhere you can stay still and warm — not while driving. You can move, stop, or open your eyes anytime.'
                    : 'Rest somewhere safe — not while driving. You can stop or open your eyes anytime.'}</p>
                  {/* Mode notes live INSIDE the scrolling ready content. They used
                      to render after the controls row, which is now pinned to the
                      bottom of a non-scrolling page — so out there they'd be
                      clipped entirely. */}
                  {mode === 'sleep' && (
                    <p style={s.modeNote}>
                      My voice gently guides you down while you're still awake — when calming suggestions settle in most easily — then fades as you drift off. A soft tone continues quietly in the background. Set your device down, dim the screen, and let yourself fall asleep naturally.
                    </p>
                  )}
                  {(config.freq || config.ambience) && (
                    <div style={s.toneWrap}>
                      <span style={s.toneLabel}>{config.ambience ? 'Background sound' : 'Background tone'}</span>
                      {config.ambience && (
                        <div style={s.toneRow}>
                          {Object.entries(AMBIENT_SCENES).map(([sc, v]) => (
                            <button key={sc} onClick={() => pickScene(sc)}
                              style={{ ...s.toneBtn, ...(ambientScene === sc ? s.toneBtnOn : {}) }}>{v.icon} {v.label}</button>
                          ))}
                        </div>
                      )}
                      <div style={s.toneRow}>
                        {[['soft','Soft'],['medium','Medium'],['off','Off']].map(([lvl, lab]) => (
                          <button key={lvl} onClick={() => pickTone(lvl)}
                            style={{ ...s.toneBtn, ...(toneLevel === lvl ? s.toneBtnOn : {}) }}>{lab}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {phase === 'playing' && spokenLine && (
                <p style={s.scriptLive}>{spokenLine}</p>
              )}
              {phase === 'complete' && (
                <div style={s.completeWrap}>
                  <p style={{ fontSize:'2rem' }}>✨</p>
                  <p style={s.completeTitle}>Session Complete</p>
                  <p style={s.completeSub}>Take a breath. Notice what has shifted.</p>
                  <MoodRow label="And now — how do you feel?" value={moodAfter} onPick={pickAfter} />
                  {moodBefore && moodAfter && (
                    <p style={s.shiftLine}>
                      {MOODS.find(m => m.v === moodBefore)?.label} → {MOODS.find(m => m.v === moodAfter)?.label}
                    </p>
                  )}

                  {/* ── Experience check ── entirely optional. Framed as normal and
                      useful, never as a symptom checklist. Nothing here is required to
                      leave: Complete & Return works untouched whether or not it's used. */}
                  {expItems.length > 0 && !expSent && (
                    <div style={s.expWrap}>
                      <p style={s.expTitle}>Did you notice any of these?</p>
                      <p style={s.expLead}>
                        Common, and worth knowing about — it helps us shape what we offer you next.
                        Tap any that apply, or skip this entirely.
                      </p>
                      <div style={s.expList}>
                        {expItems.map(item => {
                          const on = expAnswers[item.id] === true
                          return (
                            <button key={item.id} style={{ ...s.expItem, ...(on ? s.expItemOn : {}) }}
                              aria-pressed={on}
                              onClick={() => setExpAnswers(a => ({ ...a, [item.id]: !a[item.id] }))}>
                              <span style={{ ...s.expTick, ...(on ? s.expTickOn : {}) }}>{on ? '✓' : ''}</span>
                              <span style={s.expText}>{item.prompt}</span>
                            </button>
                          )
                        })}
                      </div>
                      <button style={s.expSend} onClick={() => sendExperienceCheck({ force: true })}>
                        Done
                      </button>
                    </div>
                  )}

                  {expSent && !expOffer && (
                    <p style={s.expThanks}>Thank you — that helps.</p>
                  )}

                  {/* A notable result offers the grounding screen. An OFFER, never a
                      redirect: being moved somewhere unexpectedly is the opposite of
                      what helps here, and this is not a verdict about the person. */}
                  {expOffer && (
                    <div style={s.expOfferWrap}>
                      <p style={s.expOfferText}>
                        Thank you for saying so. If any of that is still with you, a couple of
                        minutes coming back to the room can help settle it.
                      </p>
                      <button style={s.expOfferBtn} onClick={() => setPhase('grounding')}>
                        Take me through it
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Controls ── always on screen, and clear of the fixed Urgent-help
              pill and AI-disclosure bar (see .sov-bottom-safe in global.css). */}
          <div style={s.controls} className="sov-bottom-safe">
            {phase === 'ready' && (
              <button style={s.beginBtn} onClick={onBegin}>
                {config.noVoice ? '🎧 Begin Session' : mode === 'sleep' ? '🌙 Begin Sleep Induction' : mode === 'nidra' ? '🪷 Begin Yoga Nidra' : '▶ Begin Session'}
              </button>
            )}
            {phase === 'playing' && (
              <div style={s.playControls}>
                {!config.noVoice && (paused
                  ? <button style={s.resumeBtn} onClick={handleResume}>&#9654; Resume</button>
                  : <button style={s.pauseBtn} onClick={handlePause}>&#10073;&#10073; Pause</button>
                )}
                <button style={{ ...s.stopBtn, flex:1, width:'auto' }} onClick={handleStop}>&#9632; Stop</button>
              </div>
            )}
            {/* Below the row, not competing with it: Stop is "I'm done", this is
                "get me out of this properly". Quieter, always visible while playing. */}
            {phase === 'playing' && (
              <button style={s.groundBtn} onClick={handleGround}>
                Bring me back
              </button>
            )}
            {phase === 'complete' && (
              <button style={s.beginBtn} onClick={() => {
                // If they ticked items but never pressed Done, send it anyway rather than
                // silently discarding what they told us. Fire-and-forget — leaving is
                // never blocked on it. No-op when nothing was ticked or it already sent.
                sendExperienceCheck()
                logSession(true); freqRef.current?.stop(); onComplete?.()
              }}>
                ✓ Complete &amp; Return
              </button>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulse { 0%,100%{opacity:.3;transform:scale(1)} 50%{opacity:.8;transform:scale(1.08)} }
        /* Nidra scene. Periods are LONG on purpose — the slowest thing on screen sets
           the tempo the body settles to, so nothing here moves faster than a resting
           breath (the glow, ~9s) and the light drifts take most of a minute. */
        @keyframes nidraDrift1 { 0%,100% { transform: translate(-6%,-4%) scale(1) } 50% { transform: translate(5%,4%) scale(1.12) } }
        @keyframes nidraDrift2 { 0%,100% { transform: translate(4%,3%) scale(1.1) } 50% { transform: translate(-5%,-3%) scale(1) } }
        @keyframes nidraFall   { 0% { background-position: 0 0 } 100% { background-position: 0 260px } }
        @keyframes nidraBreathe{ 0%,100% { opacity:.22; transform: scale(.96) } 50% { opacity:.5; transform: scale(1.04) } }
        .sov-nidra-drift1 { animation: nidraDrift1 52s ease-in-out infinite }
        .sov-nidra-drift2 { animation: nidraDrift2 68s ease-in-out infinite }
        .sov-nidra-veil   { animation: nidraFall 22s linear infinite }
        .sov-nidra-glow   { animation: nidraBreathe 9s ease-in-out infinite }
        @media (prefers-reduced-motion: reduce) {
          .sov-nidra-drift1, .sov-nidra-drift2, .sov-nidra-veil, .sov-nidra-glow { animation: none !important }
        }
      `}</style>
    </div>
  )
}

// ── Per-mode background gradients ─────────────────────────────────────────────
// Organic "deep room" palette (Board 4, 2026-08-13): every mode moves from cold
// purple to warm ink, keeping its own temperature — sleep stays the coolest and
// darkest for bedtime, frequency leans amber, theta a muted plum-ink.
const BG = {
  guided:       'linear-gradient(170deg,#2e2b25,#211e19)',
  frequency:    'linear-gradient(170deg,#2b241a,#1e1911)',
  sleep:        'linear-gradient(170deg,#20242a,#141719)',
  subconscious: 'linear-gradient(170deg,#2a252e,#1b171f)',
  // Nidra: pond-green ink, the coolest green in the set. The NidraScene layers
  // draw their light on top of this, so it reads as deep water, not a flat wall.
  nidra:        'linear-gradient(170deg,#16261e,#0c1712)',
}

const s = {
  // The page itself no longer scrolls: the SCRIPT AREA scrolls inside it, so the
  // controls row is always on screen. Before this, a tall ready screen (preview
  // + mood row + caution + tone picker) pushed "Begin Session" below the fold —
  // and because the script area centres its content, the screen LOOKED finished,
  // so there was nothing to suggest scrolling. That was the meditation dead end.
  // position + isolation give the page its own stacking context so the NidraScene
  // can sit at zIndex:-1 — above the page background, below every control — without
  // any sibling needing its own z-index. Harmless for the modes with no scene.
  page: (mode) => ({ display:'flex', flexDirection:'column', height:'100%', background: BG[mode] || BG.guided, overflow:'hidden', position:'relative', isolation:'isolate' }),
  header:        { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.25rem', borderBottom:'1px solid rgba(255,255,255,.07)', background:'rgba(0,0,0,.4)', flexShrink:0 },
  backBtn:       { background:'transparent', border:'none', color:'rgba(255,255,255,.45)', fontSize:'0.875rem', cursor:'pointer' },
  homeBtn:       { background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.7)', borderRadius:'0.5rem', padding:'0.3rem 0.6rem', fontSize:'1rem', cursor:'pointer', lineHeight:1 },
  modeTag:       { fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.12em', color:'rgba(201,168,76,.85)' },
  liveTag:       { fontSize:'0.72rem', color:'rgba(255,255,255,.4)', letterSpacing:'0.04em', fontFamily:'monospace' },
  centre:        { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem', padding:'2rem' },

  // ── Grounding exit ── deliberately plain, warm and unhurried. No red, no alarm
  // styling, no urgency: this is a calm off-ramp, not an incident. Type is a step
  // larger and line-height looser than the rest of the player because the person
  // reading it may be disoriented and is often reading it without their glasses on.
  groundBtn:     { width:'100%', marginTop:'0.75rem', background:'rgba(201,168,76,.10)', border:'1px solid rgba(201,168,76,.35)', color:'rgba(245,240,232,.9)', borderRadius:'0.75rem', padding:'0.7rem 1rem', fontSize:'0.9rem', cursor:'pointer', letterSpacing:'0.02em' },
  groundWrap:    { flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:'1rem', padding:'2rem 1.5rem', maxWidth:560, margin:'0 auto', width:'100%' },
  groundTitle:   { fontSize:'1.35rem', fontWeight:600, color:'#f5f0e8', lineHeight:1.4 },
  groundLead:    { fontSize:'0.95rem', color:'rgba(245,240,232,.6)', lineHeight:1.6 },
  groundList:    { display:'flex', flexDirection:'column', gap:'0.9rem', paddingLeft:'1.3rem', margin:'0.5rem 0' },
  groundStep:    { fontSize:'1.02rem', color:'rgba(245,240,232,.88)', lineHeight:1.65 },
  groundNote:    { fontSize:'0.88rem', color:'rgba(245,240,232,.55)', lineHeight:1.7, borderLeft:'2px solid rgba(201,168,76,.35)', paddingLeft:'0.9rem', marginTop:'0.5rem' },
  // ── Experience check ── styled as a quiet, ordinary part of the wrap-up. No warning
  // colours, no icons, no clinical framing: it must not read as a symptom checklist or
  // people will either not answer it or answer it anxiously, and both ruin the data.
  expWrap:       { marginTop:'1.5rem', textAlign:'left', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.1rem' },
  expTitle:      { fontSize:'0.95rem', fontWeight:600, color:'#f5f0e8', marginBottom:'0.4rem' },
  expLead:       { fontSize:'0.8rem', color:'rgba(245,240,232,.5)', lineHeight:1.6, marginBottom:'0.9rem' },
  expList:       { display:'flex', flexDirection:'column', gap:'0.5rem' },
  expItem:       { display:'flex', alignItems:'flex-start', gap:'0.7rem', background:'transparent', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.7rem', padding:'0.6rem 0.75rem', textAlign:'left', cursor:'pointer', width:'100%', transition:'all .15s' },
  expItemOn:     { background:'rgba(201,168,76,.10)', border:'1px solid rgba(201,168,76,.45)' },
  expTick:       { flexShrink:0, width:18, height:18, borderRadius:'0.3rem', border:'1px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', color:'#1a0830', marginTop:2 },
  expTickOn:     { background:'#c9a84c', border:'1px solid #c9a84c' },
  expText:       { fontSize:'0.83rem', color:'rgba(245,240,232,.8)', lineHeight:1.5 },
  expSend:       { marginTop:'0.9rem', width:'100%', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.18)', color:'#f5f0e8', borderRadius:'0.7rem', padding:'0.6rem 1rem', fontSize:'0.85rem', cursor:'pointer' },
  expThanks:     { marginTop:'1.25rem', fontSize:'0.85rem', color:'rgba(245,240,232,.5)' },
  expOfferWrap:  { marginTop:'1.4rem', textAlign:'left', borderLeft:'2px solid rgba(201,168,76,.4)', paddingLeft:'0.9rem' },
  expOfferText:  { fontSize:'0.86rem', color:'rgba(245,240,232,.7)', lineHeight:1.65, marginBottom:'0.75rem' },
  expOfferBtn:   { background:'rgba(201,168,76,.12)', border:'1px solid rgba(201,168,76,.4)', color:'#f5f0e8', borderRadius:'0.7rem', padding:'0.55rem 1rem', fontSize:'0.85rem', cursor:'pointer' },

  groundHome:    { marginTop:'1.25rem', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.2)', color:'#f5f0e8', borderRadius:'0.75rem', padding:'0.8rem 1rem', fontSize:'0.95rem', cursor:'pointer' },
  spinner:       { width:40, height:40, border:'2px solid rgba(201,168,76,.2)', borderTop:'2px solid #c9a84c', borderRadius:'50%', animation:'spin 1s linear infinite' },
  genTitle:      { fontSize:'1.05rem', fontWeight:600, color:'#f5f0e8', textAlign:'center' },
  genSub:        { fontSize:'0.8rem', color:'rgba(245,240,232,.4)', textAlign:'center' },
  retryBtn:      { padding:'0.6rem 1.5rem', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', borderRadius:'0.75rem', color:'#f5f0e8', cursor:'pointer' },
  sessionHeader: { padding:'1.5rem 1.25rem 0', textAlign:'center' },
  sessionName:   { fontSize:'1.25rem', fontWeight:700, color:'#f5f0e8', marginBottom:'0.35rem' },
  freqTag:       { fontSize:'0.75rem', color:'#c9a84c', letterSpacing:'0.03em' },
  subconsciousWrap:  { flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1.5rem 1.25rem' },
  subconsciousIntro: { fontSize:'0.9rem', color:'rgba(245,240,232,.55)', lineHeight:1.7, textAlign:'center', whiteSpace:'pre-line', marginBottom:'2rem' },
  affirmPreview:     { display:'flex', flexDirection:'column', gap:'0.75rem', width:'100%' },
  affirmLine:        { fontSize:'1rem', color:'#ffe1d0', textAlign:'center', fontStyle:'italic' },
  affirmActive:      { display:'flex', flexDirection:'column', alignItems:'center', gap:'1.5rem', flex:1, justifyContent:'center' },
  affirmCurrent:     { fontSize:'1.4rem', fontWeight:600, color:'#ffe1d0', textAlign:'center', lineHeight:1.5, padding:'0 1rem' },
  affirmNext:        { fontSize:'0.9rem', color:'rgba(255,225,208,.35)', textAlign:'center', fontStyle:'italic' },
  affirmTimer:       { fontSize:'0.7rem', color:'rgba(245,234,216,.25)', fontFamily:'monospace', letterSpacing:'0.1em' },
  freqPulse:         { width:60, height:60, borderRadius:'50%', background:'rgba(255,198,165,.14)', border:'1px solid rgba(255,198,165,.35)', animation:'pulse 3s ease-in-out infinite' },
  // minHeight:0 is load-bearing — without it a flex child refuses to shrink
  // below its content, so the overflow lands on the page instead of here and
  // the controls get pushed off-screen again.
  scriptArea:   { flex:1, minHeight:0, overflowY:'auto', padding:'1.5rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'center' },
  scriptPreview:{ fontSize:'0.975rem', color:'rgba(245,240,232,.4)', lineHeight:1.8, fontStyle:'italic', textAlign:'center' },
  scriptLive:   { fontSize:'1.1rem', color:'#f5e8d0', lineHeight:1.9, textAlign:'center', fontWeight:300, letterSpacing:'0.01em' },
  completeWrap: { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.75rem', textAlign:'center' },
  completeTitle:{ fontSize:'1.3rem', fontWeight:700, color:'#f5f0e8' },
  completeSub:  { fontSize:'0.875rem', color:'rgba(245,240,232,.5)' },
  // Bottom padding is owned by the .sov-bottom-safe class (an inline value here
  // would win over it and re-break the clearance). --sov-bottom-pad is this
  // block's own base padding, which the class adds the pill/banner room to.
  // NOT the `padding` shorthand: it emits an inline padding-bottom:0, and inline
  // beats the class, so .sov-bottom-safe's clearance would be silently discarded.
  // (Caught by the layout test — the first version of this fix did exactly that
  // and measured identical to the broken layout.)
  controls:     { paddingLeft:'1.25rem', paddingRight:'1.25rem', flexShrink:0, '--sov-bottom-pad':'0.75rem' },
  beginBtn:     { width:'100%', padding:'1rem', background:'#c67139', color:'#f5ead8', border:'none', borderRadius:'999px', fontSize:'1rem', fontWeight:600, cursor:'pointer', letterSpacing:'0.02em' },
  stopBtn:      { width:'100%', padding:'1rem', background:'rgba(245,234,216,.06)', border:'none', color:'rgba(245,234,216,.6)', borderRadius:'999px', fontSize:'1rem', cursor:'pointer' },
  modeNote:     { padding:'0 1.25rem 1.5rem', fontSize:'0.78rem', color:'rgba(245,240,232,.3)', textAlign:'center', lineHeight:1.6 },
  readyWrap:    { display:'flex', flexDirection:'column', alignItems:'center', gap:'1rem' },
  cautionLine:  { fontSize:'0.72rem', color:'rgba(245,240,232,.38)', textAlign:'center', lineHeight:1.5, maxWidth:320 },
  toneWrap:     { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.4rem' },
  toneLabel:    { fontSize:'0.7rem', color:'rgba(245,240,232,.4)', letterSpacing:'0.04em' },
  toneRow:      { display:'flex', gap:'0.4rem' },
  toneBtn:      { background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', color:'rgba(245,240,232,.6)', borderRadius:'0.6rem', padding:'0.35rem 0.8rem', fontSize:'0.78rem', cursor:'pointer' },
  toneBtnOn:    { background:'rgba(201,168,76,.2)', border:'1px solid rgba(201,168,76,.55)', color:'#f5f0e8' },
  moodWrap:     { textAlign:'center' },
  moodLabel:    { fontSize:'0.8rem', color:'rgba(245,240,232,.5)', marginBottom:'0.6rem' },
  moodRow:      { display:'flex', justifyContent:'center', gap:'0.4rem' },
  moodBtn:      { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.2rem', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'0.75rem', padding:'0.5rem 0.4rem', cursor:'pointer', minWidth:52 },
  moodBtnOn:    { background:'rgba(122,111,207,.25)', border:'1px solid rgba(155,143,232,.6)' },
  moodEmoji:    { fontSize:'1.3rem', lineHeight:1 },
  moodWord:     { fontSize:'0.62rem', color:'rgba(245,240,232,.55)', letterSpacing:'0.02em' },
  shiftLine:    { fontSize:'0.9rem', color:'#c9a84c', marginTop:'0.25rem', letterSpacing:'0.03em' },
  playControls: { display:'flex', gap:'0.625rem' },
  // ── Nidra scene layers ── all decoration: fixed behind the content (zIndex:-1
  // under the page's isolated stacking context), never interactive. Colour notes:
  // the drifts are the green and teal of light through pond water; the veil is a
  // barely-there fall of white down the right third; the glow is the warm centre
  // the eyes settle on before closing.
  nidraScene:  { position:'absolute', inset:0, overflow:'hidden', zIndex:-1, pointerEvents:'none' },
  nidraDrift1: { position:'absolute', top:'-20%', left:'-20%', width:'90%', height:'80%', borderRadius:'50%', background:'radial-gradient(closest-side, rgba(74,156,116,.16), transparent)', filter:'blur(30px)' },
  nidraDrift2: { position:'absolute', bottom:'-15%', right:'-20%', width:'85%', height:'75%', borderRadius:'50%', background:'radial-gradient(closest-side, rgba(58,124,128,.14), transparent)', filter:'blur(34px)' },
  nidraVeil:   { position:'absolute', top:0, bottom:0, right:'12%', width:'24%', opacity:.35, filter:'blur(6px)', background:'repeating-linear-gradient(180deg, rgba(220,240,232,.05) 0px, rgba(220,240,232,0) 26px, rgba(220,240,232,.03) 52px, rgba(220,240,232,0) 90px)', maskImage:'linear-gradient(90deg, transparent, #000 30%, #000 70%, transparent)', WebkitMaskImage:'linear-gradient(90deg, transparent, #000 30%, #000 70%, transparent)' },
  nidraGlow:   { position:'absolute', left:'50%', top:'58%', width:340, height:340, marginLeft:-170, marginTop:-170, borderRadius:'50%', background:'radial-gradient(closest-side, rgba(150,205,170,.20), rgba(150,205,170,.06) 55%, transparent)', filter:'blur(10px)' },
  pauseBtn:     { flex:1, padding:'1rem', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.12)', color:'rgba(245,240,232,.75)', borderRadius:'1rem', fontSize:'1rem', cursor:'pointer' },
  resumeBtn:    { flex:1, padding:'1rem', background:'linear-gradient(135deg,#7a6fcf,#9b8fe8)', color:'#fff', border:'none', borderRadius:'1rem', fontSize:'1rem', fontWeight:700, cursor:'pointer' },
}
